import type { ResourceDefinition } from 'react-admin';
import type { MenuGroup } from '../menuGroups';
import type { Actions } from '../constants/actions';
import type { Page } from '../components/layout/sidebar/SidebarNavList';

export type MenuItem = {
  title: string;
  pages: Page[];
};

type ResourceOptions = {
  label?: string;
  exact?: boolean;
};

// Single source for the sidebar entries, so the expanded sidebar and the
// collapsed icon strip always show the same permission-filtered list.
const buildMenuItems = ({
  menuGroups,
  resourceDefinitions,
  getResourceLabel,
  permissions,
}: {
  menuGroups: MenuGroup[];
  resourceDefinitions: Record<string, ResourceDefinition<ResourceOptions>>;
  getResourceLabel: (resourceName: string) => string;
  permissions?: Actions[];
}): MenuItem[] => menuGroups.map((menuGroup) => ({
  title: menuGroup.title,
  pages: menuGroup.pages.map((page): Page | null => {
    if ('title' in page) {
      const { permissions: requiredPermissions, ...rest } = page;
      if (
        !permissions
        || (requiredPermissions.length > 0
          && !requiredPermissions.some((permission) => permissions.includes(permission)))
      ) return null;
      return rest;
    }
    const resource = resourceDefinitions[page.resource];

    if (!resource || !resource.hasList) return null;

    return {
      href: `/${resource.name}`,
      openNewTab: false,
      icon: resource.icon,
      title: resource.options?.label || getResourceLabel(resource.name),
      exact: resource.options?.exact ?? false,
    };
  }).filter((page): page is Page => !!page),
}));

export default buildMenuItems;
